import React, { useState, useEffect } from 'react';
import apiClient from '../../services/api';

/**
 * Edit a stock holding's share count, cost basis and price target.
 *
 * A separate modal from EditTargetModal rather than a variant of it: that one
 * is built around option concepts (days to exit against an expiration, "avg
 * cost per contract") and is shared with the Watchlist, so branching it would
 * put portfolio logic into a component the watchlist depends on. This posts to
 * the same `update-target/` endpoint.
 *
 * Changing avg cost also rewrites the stored cost basis server-side, so every
 * % change afterwards is measured against what you actually paid.
 */
export default function EditHoldingModal({ isOpen, onClose, contract, onSave }) {
  const [formData, setFormData] = useState({
    number_of_contracts: '',
    average_cost_per_contract: '',
    target_direction: 'gain',
    target_value: '',
  });
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen && contract) {
      let direction = 'gain';
      let value = '';
      if (contract.target_percent_change != null) {
        const targetPercent = parseFloat(contract.target_percent_change);
        direction = targetPercent >= 0 ? 'gain' : 'loss';
        value = Math.abs(targetPercent).toString();
      }

      setFormData({
        number_of_contracts: contract.number_of_contracts?.toString() || '1',
        average_cost_per_contract: contract.average_cost_per_contract?.toString() || '',
        target_direction: direction,
        target_value: value,
      });
      setError('');
    }
  }, [isOpen, contract]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError('');
  };

  const handleSave = async () => {
    setError('');
    setIsSaving(true);

    try {
      const payload = {};

      if (formData.number_of_contracts.trim() !== '') {
        const shares = parseInt(formData.number_of_contracts, 10);
        if (isNaN(shares) || shares < 1) {
          setError('Number of shares must be at least 1');
          setIsSaving(false);
          return;
        }
        payload.number_of_contracts = shares;
      }

      if (formData.average_cost_per_contract.trim() !== '') {
        const avgCost = parseFloat(formData.average_cost_per_contract);
        if (isNaN(avgCost) || avgCost < 0) {
          setError('Average cost must be a positive number');
          setIsSaving(false);
          return;
        }
        payload.average_cost_per_contract = avgCost;
      }

      if (formData.target_value.trim() !== '') {
        const targetValue = parseFloat(formData.target_value);
        if (isNaN(targetValue) || targetValue < 0) {
          setError('Target percentage must be a positive number');
          setIsSaving(false);
          return;
        }
        payload.target_percent_change =
          formData.target_direction === 'loss' ? -targetValue : targetValue;
      } else {
        payload.target_percent_change = null;
      }

      const response = await apiClient.patch(
        `/saved-contracts/${contract.id}/update-target/`,
        payload
      );

      if (onSave) onSave(response.data.contract);
      onClose();
    } catch (err) {
      console.error('❌ Error updating holding:', err);
      setError(err.response?.data?.error || err.message || 'Failed to update holding');
    } finally {
      setIsSaving(false);
    }
  };

  if (!isOpen || !contract) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-70 flex items-center justify-center z-50">
      <div className="pf-modal p-6 max-w-lg w-full mx-4 shadow-2xl">
        <h3 className="text-xl font-bold text-white mb-4">
          Edit Holding — {contract.ticker}
        </h3>

        {error && (
          <div className="bg-red-900/30 border border-red-500 text-red-300 px-4 py-2 rounded mb-4">
            {error}
          </div>
        )}

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-[color:var(--pf-label)] mb-1">
              Number of Shares
            </label>
            <input
              type="number"
              min="1"
              className="input w-full"
              value={formData.number_of_contracts}
              onChange={(e) => handleChange('number_of_contracts', e.target.value)}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[color:var(--pf-label)] mb-1">
              Avg Cost per Share
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              className="input w-full"
              value={formData.average_cost_per_contract}
              onChange={(e) => handleChange('average_cost_per_contract', e.target.value)}
            />
            <p className="text-xs text-[color:var(--pf-faint)] mt-1">
              Your actual purchase price. Changing this resets the cost basis every
              % change is measured against.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-[color:var(--pf-label)] mb-1">
              Price Target <span className="text-[color:var(--pf-faint)]">(optional)</span>
            </label>
            <div className="flex gap-2">
              <select
                className="input"
                value={formData.target_direction}
                onChange={(e) => handleChange('target_direction', e.target.value)}
              >
                <option value="gain">Gain</option>
                <option value="loss">Loss</option>
              </select>
              <input
                type="number"
                step="0.1"
                min="0"
                placeholder="%"
                className="input flex-1"
                value={formData.target_value}
                onChange={(e) => handleChange('target_value', e.target.value)}
              />
            </div>
            <p className="text-xs text-[color:var(--pf-faint)] mt-1">
              Leave blank to clear the target.
            </p>
          </div>
        </div>

        <div className="flex justify-end gap-2 mt-6">
          <button className="btn-dark" onClick={onClose} disabled={isSaving}>
            Cancel
          </button>
          <button className="btn-primary" onClick={handleSave} disabled={isSaving}>
            {isSaving ? 'Saving...' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
}
